import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import DropdownMenu, {
	DropdownItemCheckbox,
	DropdownItemCheckboxGroup,
} from '@atlaskit/dropdown-menu';
import Button from '@atlaskit/button/new';
import Badge from '@atlaskit/badge';
import { Inline } from '@atlaskit/primitives/compiled';
import PagesIcon from '@atlaskit/icon/core/pages';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';

const i18n = defineMessages({
	triggerLabel: {
		id: 'performance-insights.content-metrics.filters.type.trigger-label.non-final',
		defaultMessage: 'Type',
		description:
			'Label for the Type filter dropdown trigger button in the content metrics view controls bar. Opens a dropdown to filter content by type (Page, Live doc, Blog).',
	},
	page: {
		id: 'performance-insights.content-metrics.filters.type.page.non-final',
		defaultMessage: 'Page',
		description: 'Checkbox label in the Type filter dropdown for filtering by Confluence pages.',
	},
	liveDoc: {
		id: 'performance-insights.content-metrics.filters.type.live-doc.non-final',
		defaultMessage: 'Live doc',
		description:
			'Checkbox label in the Type filter dropdown for filtering by Confluence live documents.',
	},
	blog: {
		id: 'performance-insights.content-metrics.filters.type.blog.non-final',
		defaultMessage: 'Blog',
		description:
			'Checkbox label in the Type filter dropdown for filtering by Confluence blog posts.',
	},
});

export type ContentType = 'page' | 'live-doc' | 'blog';

const TYPE_I18N_MAP: Record<ContentType, (typeof i18n)[keyof typeof i18n]> = {
	page: i18n.page,
	'live-doc': i18n.liveDoc,
	blog: i18n.blog,
};

export type TypeFilterProps = {
	selectedTypes: ContentType[];
	onSelectionChange: (selectedTypes: ContentType[]) => void;
};

export const TypeFilter = ({ selectedTypes, onSelectionChange }: TypeFilterProps) => {
	const { formatMessage } = useIntl();

	const handleClick = (type: ContentType) => {
		if (selectedTypes.includes(type)) {
			onSelectionChange(selectedTypes.filter((t) => t !== type));
		} else {
			onSelectionChange([...selectedTypes, type]);
		}
	};

	const hasSelections = selectedTypes.length > 0;
	const extraCount = selectedTypes.length - 1;

	const getTriggerLabel = () => {
		if (!hasSelections) {
			return formatMessage(i18n.triggerLabel);
		}
		const firstSelected = formatMessage(TYPE_I18N_MAP[selectedTypes[0]]);
		return `${formatMessage(i18n.triggerLabel)}: ${firstSelected}`;
	};

	return (
		<DropdownMenu
			trigger={({ triggerRef, ...props }) => (
				<Button
					ref={triggerRef as React.Ref<HTMLButtonElement>}
					{...props}
					isSelected={hasSelections}
					iconBefore={(iconProps) => <PagesIcon {...iconProps} label="" />}
					iconAfter={(iconProps) => <ChevronDownIcon {...iconProps} label="" />}
				>
					<Inline alignBlock="center" space="space.050">
						{getTriggerLabel()}
						{extraCount > 0 && <Badge appearance="primary">{`+${extraCount}`}</Badge>}
					</Inline>
				</Button>
			)}
			placement="bottom-start"
		>
			<DropdownItemCheckboxGroup id="type-filter">
				<DropdownItemCheckbox
					id="type-page"
					isSelected={selectedTypes.includes('page')}
					onClick={() => handleClick('page')}
				>
					{formatMessage(i18n.page)}
				</DropdownItemCheckbox>
				<DropdownItemCheckbox
					id="type-live-doc"
					isSelected={selectedTypes.includes('live-doc')}
					onClick={() => handleClick('live-doc')}
				>
					{formatMessage(i18n.liveDoc)}
				</DropdownItemCheckbox>
				<DropdownItemCheckbox
					id="type-blog"
					isSelected={selectedTypes.includes('blog')}
					onClick={() => handleClick('blog')}
				>
					{formatMessage(i18n.blog)}
				</DropdownItemCheckbox>
			</DropdownItemCheckboxGroup>
		</DropdownMenu>
	);
};
